import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "~/components/forms/SelectInput";
import BetterNumberInput from "~/components/forms/BetterNumberInput";
import invariant from "tiny-invariant";
import Label from "~/components/Label";

/**
 * TODO When decrement down to 0 - it loops and errors
 * TODO Prevent looping
 * TODO Implement adjusting or rounding precision
 * TODO Implement generate results permalink widget with coping to clipboard
 */
export enum Unit {
  // TODO Add more units
  // angstrom = "angstrom",
  // nano = "nano",
  // micron = "micron",
  mm = "mm",
  cm = "cm",
  inches = "inches",
  feet = "feet",
  // yd = "yd",
  meters = "meters",
  // km = "km",
  // mi = "mi",
  // nmi = "nmi",
  // au = "au",
  // ly = "ly",
  // pc = "pc"
}

interface Props {
  from: Unit,
  to: Unit,
  value: number,
  onChange?: (event: StateChangeEvent) => void,
}

interface StateChangeEvent {
  numberA: number,
  unitA: Unit,
  numberB: number,
  unitB: Unit
}

const LengthCalculator: React.FC<Props> = (
  {
    from,
    to,
    value = 1,
    onChange = () => {}
  }
) => {
  const { t } = useTranslation("translations");
  const TKEY: string = "calculators.length";

  const [numberA, setNumberA] = useState<number>(value);
  const [unitA, setUnitA] = useState<Unit>(from);
  const [numberB, setNumberB] = useState<number>(0);
  const [unitB, setUnitB] = useState<Unit>(to);


  useEffect(() => {
    calculate(numberA, null);
  }, []);

  useEffect(() => {
    console.debug("Unit changed. Number A", numberA)
    calculate(numberA, null);
  }, [unitA, unitB]);


  const unitsSelectorOptions = Object.values(Unit).map(unit => ({
    value: unit,
    name: t(`${TKEY}.units.${unit}`)
  }));

  const conversionFactors: { [key: string]: number } = {
    "nano-angstrom": 10,
    "micron-angstrom": 10000,
    "micron-nano": 1000,
    "mm-angstrom": 10000000,
    "mm-nano": 1000000,
    "mm-micron": 1000,
    "cm-angstrom": 100000000,
    "cm-nano": 10000000,
    "cm-micron": 10000,
    "cm-mm": 10,
    "inches-angstrom": 25400000,
    "inches-nano": 25400000,
    "inches-micron": 25400,
    "inches-mm": 25.4,
    "inches-cm": 2.54,
    "feet-angstrom": 304800000,
    "feet-nano": 304800000,
    "feet-micron": 304800,
    "feet-mm": 304.8,
    "feet-cm": 30.48,
    "feet-inches": 12,
    "yd-angstrom": 914400000,
    "yd-nano": 914400000,
    "yd-micron": 914400,
    "yd-mm": 914.4,
    "yd-cm": 91.44,
    "yd-inches": 36,
    "yd-feet": 3,
    "meters-angstrom": 1000000000,
    "meters-nano": 1000000000,
    "meters-micron": 1000000,
    "meters-mm": 1000,
    "meters-cm": 100,
    "meters-inches": 39.3701,
    "meters-feet": 3.28084,
    "meters-yd": 1.09361,
    "meters-km": 0.001,
    "km-angstrom": 1000000000000,
    "km-nano": 1000000000000,
    "km-micron": 1000000000,
    "km-mm": 1000000,
    "km-cm": 100000,
    "km-inches": 39370.1,
    "km-mi": 0.621371,
    "km-feet": 3280.84,
    "mi-angstrom": 1609344000000,
    "mi-nano": 1609344000000,
    "mi-mm": 1609344,
    "mi-cm": 160934,
    "mi-inches": 63360,
    "mi-feet": 5280,
    "mi-meters": 1609.34,
    "mi-yd": 1760,
    "mi-km": 1.60934,
    "mi-nmi": 0.868976,
    "nmi-mi": 1.15078,
    "nmi-au": 8.23158e-9,
    "au-nmi": 1.215e8,
    "au-ly": 0.000015812507409,
    "ly-au": 63241.1,
    "ly-pc": 0.306601,
    "pc-ly": 3.26156
  };

  // TODO Make adjustable
  const ROUNDING_PRECISION = 3;

  function calculate(a: number | null, b: number | null): void {
    if ((a === null && b === null) || (a !== null && b !== null)) {
      throw Error("Must provider either A or B, not both");
    }
    const reverse = a === null;
    const key = reverse ? `${unitB}-${unitA}` : `${unitA}-${unitB}`;
    const reverseKey = reverse ? `${unitA}-${unitB}` : `${unitB}-${unitA}`;

    console.debug("Recalculating: ", key, a, b);
    const factor = unitA === unitB
      ? 1
      : (conversionFactors[key] ?? 1 / conversionFactors[reverseKey])
    ;

    const round = Math.pow(10, ROUNDING_PRECISION);
    if (reverse) {
      invariant(b !== null);
      a = Math.round(b * factor * round) / round;
      // console.log("RESULT A", b*factor, String(a));
      setNumberA(a);
      setNumberB(b);
    } else {
      invariant(a !== null);
      b = Math.round(a * factor * round) / round;
      // console.log("RESULT B", a * factor, String(b));

      setNumberA(a);
      setNumberB(b);
    }

    onChange({ numberA: a, unitA, numberB: b, unitB });
  }

  return (
    <div className="flex flex-col md:flex-row md:gap-6 items-center">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full md:max-w-[200px] lg:max-w-[220px]">
          <Label htmlFor="numberA" text={t(`${TKEY}.from`)} />
          <BetterNumberInput
            name="numberA"
            value={numberA}
            onChange={(value) => calculate(Number(value), null)}
            min={0.0000001}
            maxFractionDigits={ROUNDING_PRECISION}
            ariaLabel={t(`${TKEY}.from`)}
            autoFocus={true}
          />
        </div>
        <div className="w-full md:max-w-[200px] lg:w-[160px]">
          <Label htmlFor={"unitA"} text={t(`${TKEY}.unit`)} />
          <SelectInput
            name="unitA"
            options={unitsSelectorOptions}
            value={unitsSelectorOptions.find((unit) => unit.value === unitA)?.value}
            ariaLabel={t(`${TKEY}.unit`)}
            onChange={(value) => setUnitA(value as Unit)}
          />
        </div>
      </div>
      <div className="text-6xl pt-4 text-slate-500 dark:text-slate-400">
        <span className="hidden md:inline">
          ⬌
        </span>
        <span className="md:hidden">
          ⬍
        </span>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full md:max-w-[200px] lg:max-w-[220px]">
          <Label htmlFor="numberB" text={t(`${TKEY}.to`)} />
          <BetterNumberInput
            name="numberB"
            value={numberB}
            onChange={(value) => calculate(null, Number(value))}
            min={0.0000001}
            maxFractionDigits={ROUNDING_PRECISION}
            ariaLabel={t(`${TKEY}.to`)}
            autoFocus={true}
          />
        </div>
        <div className="w-full md:max-w-[200px] lg:w-[160px]">
          <Label htmlFor={"unitB"} text={t(`${TKEY}.unit`)} />
          <SelectInput
            name="unitA"
            options={unitsSelectorOptions}
            value={unitsSelectorOptions.find((unit) => unit.value === unitB)?.value}
            ariaLabel={t(`${TKEY}.unit`)}
            onChange={(value) => setUnitB(value as Unit)}
          />
        </div>
      </div>
    </div>
  );
};

export default LengthCalculator;